/**
 * Firestore Adapter for UnifiedTransactions
 *
 * Mirrors the current Realtime DB "unified" storage system but leverages
 * Firestore's native querying and automatic totals calculation.
 *
 * Structure:
 * /companies/{companyId}/transactions/{transactionId}
 * /companies/{companyId}/accountTotals/monthly/{year}-{month}  // e.g., "2024-03"
 * /companies/{companyId}/accountTotals/yearly/{year}           // e.g., "2024"
 *
 * Account Totals Structure (per month and per year):
 * {
 *   period: "2024-03" or "2024",           // Month or year identifier
 *   periodType: "monthly" or "yearly",     // Type of aggregation
 *
 *   // PER-ACCOUNT BALANCES: Enables showing account-level detail without downloading transactions
 *   accounts: {
 *     "Sales": { category: "Income", balance: 50000, balanceVatExclusive: 43478, vatAmount: 6522, count: 5 },
 *     "Fees": { category: "Income", balance: 25000, balanceVatExclusive: 25000, vatAmount: 0, count: 3 },
 *     "Bank": { category: "Current Assets", balance: 100000, balanceVatExclusive: 100000, vatAmount: 0, count: 10 },
 *     "Rent": { category: "Expenses", balance: 15000, balanceVatExclusive: 13043, vatAmount: 1957, count: 2 },
 *     // ... every account with balance (gross), balanceVatExclusive (net), and vatAmount
 *   },
 *
 *   // ACCOUNT GROUP TOTALS: Pre-aggregated category sums
 *   accountGroups: {
 *     Income: { balance: 150000, count: 250, accounts: ["Sales", "Fees", "Services"] },
 *     Expenses: { balance: -95000, count: 1200, accounts: ["Rent", "Salaries", "Utilities"] },
 *     "Current Assets": { balance: 250000, count: 80, accounts: ["Bank", "Debtors"] },
 *     "Current Liabilities": { balance: -50000, count: 45, accounts: ["Creditors", "Loans"] },
 *     Equity: { balance: 100000, count: 30, accounts: ["Share Capital", "Retained Earnings"] },
 *     "No Account": { balance: 0, count: 15, accounts: [] }
 *   },
 *
 *   // Metadata
 *   lastUpdated: Timestamp,
 *   transactionCount: 1620,
 *   lastRecalculated: Timestamp
 * }
 *
 * Note: Financial pages calculate their own totals (SOFP, SOCI, etc.) from accountGroups.
 * Mock transactions are generated from accounts for backward compatibility with existing filter/reduce patterns.
 *
 * Usage Strategy:
 * - Store monthly totals: /accountTotals/monthly/2024-03
 * - Store yearly rollups: /accountTotals/yearly/2024
 * - For specific day filters: Fetch latest month totals + calculate delta from month start to specific date
 * - For month filters: Direct read from monthly totals
 * - For year filters: Direct read from yearly totals
 * - For multi-year: Read multiple yearly totals and aggregate
 */

import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  writeBatch,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  increment,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const db = getFirestore();

/**
 * Increment transaction count for a user in a company
 * @param {string} companyId
 * @param {string} uid
 * @param {number} count
 */
export const incrementUserTransactionCount = async (companyId, uid, count) => {
  if (!companyId || !uid || !count) return;
  try {
    const userStatsRef = doc(db, "companies", companyId, "user_stats", uid);
    await setDoc(
      userStatsRef,
      {
        transactionCount: increment(count),
        lastUpdated: serverTimestamp(),
        uid: uid,
      },
      { merge: true },
    );
    console.log(
      `Updated transaction count for user ${uid} in company ${companyId} by ${count}`,
    );
  } catch (error) {
    console.error("Error updating user transaction count:", error);
  }
};

/**
 * Bulk write transactions with automatic totals calculation
 * @param {string} companyId - Company key
 * @param {Array} transactions - Array of transaction objects
 * @param {Object} options - { clearExisting: false, calculateTotals: true }
 * @returns {Promise<{success: boolean, written: number, totalsUpdated: boolean}>}
 */
export const bulkWriteTransactions = async (
  companyId,
  transactions,
  options = {},
) => {
  const { clearExisting = false, calculateTotals = true } = options;

  if (!companyId || !Array.isArray(transactions) || transactions.length === 0) {
    throw new Error("Invalid companyId or transactions array");
  }

  // Track comprehensive totals per month AND per year
  const totalsByMonth = {};
  const totalsByYear = {};
  let written = 0;

  try {
    // Ensure company owner doc exists to satisfy rules that require ownership
    const auth = getAuth();
    const currentUser = auth.currentUser;

    console.log("🔐 [Firestore Debug] Auth State:", {
      authenticated: !!currentUser,
      uid: currentUser?.uid,
      email: currentUser?.email,
      companyId,
    });

    try {
      const companyDocRef = doc(db, `companies/${companyId}`);
      const companySnap = await getDoc(companyDocRef);

      if (currentUser) {
        const ownerUid = currentUser.uid;
        if (!companySnap.exists() || !companySnap.data()?.uid) {
          await setDoc(companyDocRef, { uid: ownerUid }, { merge: true });
        }
      }
    } catch (ownershipErr) {
      console.error(
        "⚠️ [Firestore Debug] Company owner setup failed:",
        ownershipErr,
      );
    }

    // Split transactions into chunks (Firestore batch limit is 500)
    const batchSize = 450;
    const chunks = [];
    for (let i = 0; i < transactions.length; i += batchSize) {
      chunks.push(transactions.slice(i, i + batchSize));
    }

    console.log(
      `🔄 Firestore: Batch writing ${transactions.length} transactions in ${chunks.length} chunks`,
    );

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const batch = writeBatch(db);
      let chunkWritten = 0;

      for (const tx of chunk) {
        if (!tx || !tx.id) continue;

        const txRef = doc(db, `companies/${companyId}/transactions/${tx.id}`);
        const txData = {
          ...tx,
          updatedAt: Timestamp.now(),
        };

        batch.set(txRef, txData, { merge: true });
        chunkWritten++;
        written++;

        // Accumulate totals for financial statements (both monthly and yearly)
        if (calculateTotals && tx.date && tx.yearEndDate) {
          const year = tx.yearEndDate.slice(0, 4);
          const month = tx.date.slice(0, 7); // "2024-03"
          const accountGroup = tx.Account_group || "No Account";
          const accountName =
            tx.Account || tx.account || tx.accountNames || accountGroup;
          const amount = Number(tx.Opening_balance) || 0;
          const amountVatExclusive =
            Number(tx.Opening_balance_Vat_Exclusive) || amount;
          const vatAmount = Number(tx.vatAmount) || 0;

          // Initialize monthly totals structure
          if (!totalsByMonth[month]) {
            totalsByMonth[month] = {
              period: month,
              periodType: "monthly",
              accounts: {}, // Per-account balances
              accountGroups: {}, // Category aggregates
            };
          }

          // Initialize yearly totals structure
          if (!totalsByYear[year]) {
            totalsByYear[year] = {
              period: year,
              periodType: "yearly",
              accounts: {}, // Per-account balances
              accountGroups: {}, // Category aggregates
            };
          }

          // Helper function to update totals for a period
          const updatePeriodTotals = (periodTotals) => {
            // Store per-account balance (gross, VAT-exclusive, and VAT amount)
            if (!periodTotals.accounts[accountName]) {
              periodTotals.accounts[accountName] = {
                category: accountGroup,
                balance: 0,
                balanceVatExclusive: 0,
                vatAmount: 0,
                count: 0,
              };
            }
            periodTotals.accounts[accountName].balance += amount;
            periodTotals.accounts[accountName].balanceVatExclusive +=
              amountVatExclusive;
            periodTotals.accounts[accountName].vatAmount += vatAmount;
            periodTotals.accounts[accountName].count += 1;

            // Aggregate by account group (category)
            if (!periodTotals.accountGroups[accountGroup]) {
              periodTotals.accountGroups[accountGroup] = {
                balance: 0,
                count: 0,
                accounts: [],
              };
            }
            periodTotals.accountGroups[accountGroup].balance += amount;
            periodTotals.accountGroups[accountGroup].count += 1;

            // Track which accounts belong to this group
            if (
              !periodTotals.accountGroups[accountGroup].accounts.includes(
                accountName,
              )
            ) {
              periodTotals.accountGroups[accountGroup].accounts.push(
                accountName,
              );
            }
          };

          // Update both monthly and yearly totals
          updatePeriodTotals(totalsByMonth[month]);
          updatePeriodTotals(totalsByYear[year]);
        }
      }

      await batch.commit();
      console.log(
        `✅ Firestore: Chunk ${i + 1}/${chunks.length} committed (${chunkWritten} transactions)`,
      );
    }

    // Write totals to Firestore in separate batches
    if (calculateTotals) {
      const totalOperations = [];

      // Prepare monthly totals operations
      for (const [month, totals] of Object.entries(totalsByMonth)) {
        const totalsRef = doc(
          db,
          `companies/${companyId}/accountTotals_monthly`,
          month,
        );

        const data = clearExisting
          ? {
              ...totals,
              lastUpdated: Timestamp.now(),
              transactionCount: written,
              lastRecalculated: Timestamp.now(),
            }
          : {
              ...totals,
              lastUpdated: Timestamp.now(),
              transactionCount: increment(written),
              lastRecalculated: Timestamp.now(),
            };

        totalOperations.push({ ref: totalsRef, data, merge: !clearExisting });
      }

      // Prepare yearly totals operations
      for (const [year, totals] of Object.entries(totalsByYear)) {
        const totalsRef = doc(
          db,
          `companies/${companyId}/accountTotals_yearly`,
          year,
        );

        const data = clearExisting
          ? {
              ...totals,
              lastUpdated: Timestamp.now(),
              transactionCount: written,
              lastRecalculated: Timestamp.now(),
            }
          : {
              ...totals,
              lastUpdated: Timestamp.now(),
              transactionCount: increment(written),
              lastRecalculated: Timestamp.now(),
            };

        totalOperations.push({ ref: totalsRef, data, merge: !clearExisting });
      }

      // Execute totals operations in batches
      const totalsChunks = [];
      for (let i = 0; i < totalOperations.length; i += batchSize) {
        totalsChunks.push(totalOperations.slice(i, i + batchSize));
      }

      for (let i = 0; i < totalsChunks.length; i++) {
        const batch = writeBatch(db);
        totalsChunks[i].forEach((op) => {
          if (op.merge) {
            batch.set(op.ref, op.data, { merge: true });
          } else {
            batch.set(op.ref, op.data);
          }
        });
        await batch.commit();
        console.log(
          `✅ Firestore: Totals chunk ${i + 1}/${totalsChunks.length} committed`,
        );
      }
    }

    console.log(
      `✅ Firestore: Wrote ${written} transactions, updated totals for ${
        Object.keys(totalsByYear).length
      } years`,
    );

    // Increment user transaction count
    if (currentUser && written > 0) {
      await incrementUserTransactionCount(companyId, currentUser.uid, written);
    }

    return {
      success: true,
      written,
      totalsUpdated: calculateTotals,
      years: Object.keys(totalsByYear),
    };
  } catch (error) {
    console.error("❌ [Firestore Debug] bulkWriteTransactions error:", {
      message: error.message,
      code: error.code,
      companyId,
      transactionsAttempted: transactions.length,
      transactionsWritten: written,
      authUser: getAuth().currentUser?.uid || "none",
      errorDetails: error,
    });

    // Log specific permission error context
    if (
      error.code === "permission-denied" ||
      error.message?.includes("permission")
    ) {
      console.error("🔒 [Firestore Debug] Permission Error Details:", {
        hint: "Check that firestore.rules are deployed and company owner doc exists",
        companyPath: `companies/${companyId}`,
        transactionsPath: `companies/${companyId}/transactions`,
        totalsPath: `companies/${companyId}/accountTotals_*`,
        requiredRule:
          "Rules must allow write when request.auth.uid matches company.uid",
      });
    }

    throw error;
  }
};

/**
 * Get transactions with native Firestore queries
 * @param {string} companyId
 * @param {Object} filters - { startDate, endDate, accountGroup, bankAccount, assessed, limit, orderBy }
 * @returns {Promise<Array>}
 *
 * assessed filter options:
 * - true: Only assessed transactions (historical tab - assessed === true)
 * - false: Only NEW unassessed transactions (new tab - assessed field doesn't exist OR assessed === false)
 * - null/undefined: All transactions (for reports/pages that need full data)
 *
 * Note: Requires Firestore composite index on (date, assessed) for optimal performance
 * This filter should ONLY be used on the Accounts page, not on reports
 */
export const getTransactions = async (companyId, filters = {}) => {
  if (!companyId) {
    throw new Error("companyId is required");
  }

  const {
    startDate,
    endDate,
    accountGroup,
    bankAccount,
    assessed, // Filter by assessed status (only for Accounts page)
    limit = null,
    orderByField = "date",
    orderDirection = "asc",
  } = filters;

  try {
    let q = collection(db, `companies/${companyId}/transactions`);
    const constraints = [];

    // Apply filters
    if (startDate) constraints.push(where("date", ">=", startDate));
    if (endDate) constraints.push(where("date", "<=", endDate));
    if (accountGroup)
      constraints.push(where("Account_group", "==", accountGroup));
    if (bankAccount) constraints.push(where("bankAccount", "==", bankAccount));

    // Filter by assessed status (Accounts page only)
    if (assessed === true) {
      // Historical tab: only assessed transactions
      constraints.push(where("assessed", "==", true));
    } else if (assessed === false) {
      // New tab: only transactions where assessed field doesn't exist
      // Firestore doesn't have a "field not exists" query, so we need to:
      // 1. Fetch all transactions (with date filters)
      // 2. Filter client-side for missing assessed field
      // This is acceptable since new transactions are typically a small subset
      // Alternative: We could add a "needsAssessment: true" field at creation time
    }

    // Add ordering
    constraints.push(orderBy(orderByField, orderDirection));

    // Apply limit
    if (limit) constraints.push(firestoreLimit(limit));

    q = query(q, ...constraints);

    const snapshot = await getDocs(q);
    let transactions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore Timestamp to ISO string for consistency
      updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString?.() || null,
    }));

    // NEW TAB: Filter client-side for unassessed transactions
    // Include both missing field AND explicit false value
    if (assessed === false) {
      transactions = transactions.filter(
        (tx) =>
          tx.assessed === undefined ||
          tx.assessed === null ||
          tx.assessed === false,
      );
      console.log(
        `📋 Filtered to ${transactions.length} transactions where assessed field is missing (new transactions)`,
      );
    }

    console.log(
      `✅ Firestore: Loaded ${transactions.length} transactions for ${companyId} (${snapshot.size} reads)`,
      {
        filters,
        assessedFilter:
          assessed !== undefined ? (assessed ? "historical" : "new") : "all",
      },
    );
    return transactions;
  } catch (error) {
    console.error("❌ Firestore getTransactions error:", error);
    throw error;
  }
};

/**
 * Get pre-calculated account totals for a period (month or year)
 * @param {string} companyId
 * @param {string} period - e.g., "2024" (year) or "2024-03" (month)
 * @param {string} periodType - "monthly" or "yearly" (auto-detected if not provided)
 * @returns {Promise<Object>} - Comprehensive totals object
 */
export const getAccountTotals = async (
  companyId,
  period,
  periodType = null,
) => {
  if (!companyId || !period) {
    throw new Error("companyId and period are required");
  }

  try {
    // Auto-detect period type if not provided
    const detectedType =
      periodType || (period.includes("-") ? "monthly" : "yearly");
    const collectionName =
      detectedType === "monthly"
        ? "accountTotals_monthly"
        : "accountTotals_yearly";

    const totalsRef = doc(
      db,
      `companies/${companyId}/${collectionName}`,
      period,
    );
    const totalsDoc = await getDoc(totalsRef);

    if (!totalsDoc.exists()) {
      console.warn(
        `⚠️ No ${detectedType} totals found for ${companyId}/${period} (1 Firestore read - document not found)`,
      );
      return null;
    }

    const data = totalsDoc.data();
    // Convert Firestore Timestamps
    if (data.lastUpdated?.toDate) {
      data.lastUpdated = data.lastUpdated.toDate().toISOString();
    }
    if (data.lastRecalculated?.toDate) {
      data.lastRecalculated = data.lastRecalculated.toDate().toISOString();
    }

    console.log(
      `✅ Firestore: Retrieved ${detectedType} totals for ${companyId}/${period} (1 Firestore read)`,
      { accountCount: Object.keys(data.accounts || {}).length },
    );
    return data;
  } catch (error) {
    console.error("❌ Firestore getAccountTotals error:", error);
    throw error;
  }
};

/**
 * Get monthly totals for a specific month
 * @param {string} companyId
 * @param {string} month - e.g., "2024-03"
 * @returns {Promise<Object>}
 */
export const getMonthlyTotals = async (companyId, month) => {
  return await getAccountTotals(companyId, month, "monthly");
};

/**
 * Get yearly totals for a specific year (DEPRECATED)
 * @deprecated Yearly totals based on calendar year don't match financial year boundaries.
 * Use monthly totals and merge periods matching your financial year instead.
 * @param {string} companyId
 * @param {string} year - e.g., "2024"
 * @returns {Promise<null>}
 */
export const getYearlyTotals = async (companyId, year) => {
  console.warn(
    `⚠️ getYearlyTotals is deprecated. Calendar year "${year}" doesn't match financial year boundaries.`,
  );
  console.warn(
    `   Use monthly totals instead: merge periods matching your FY (e.g., 2024-03 through 2025-02).`,
  );
  return null;
};

/**
 * Get all account totals for a company (monthly only - yearly totals deprecated)
 * @param {string} companyId
 * @param {Object} options - { includeMonthly: true }
 * @returns {Promise<Object>} - { monthly: { "2024-03": {...} } }
 */
export const getAllAccountTotals = async (companyId, options = {}) => {
  const { includeMonthly = true } = options;

  if (!companyId) {
    throw new Error("companyId is required");
  }

  try {
    const result = { monthly: {} };

    // Fetch monthly totals
    if (includeMonthly) {
      const monthlyCollection = collection(
        db,
        `companies/${companyId}/accountTotals_monthly`,
      );
      const monthlySnapshot = await getDocs(monthlyCollection);

      monthlySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        // Convert Firestore Timestamps
        if (data.lastUpdated?.toDate) {
          data.lastUpdated = data.lastUpdated.toDate().toISOString();
        }
        if (data.lastRecalculated?.toDate) {
          data.lastRecalculated = data.lastRecalculated.toDate().toISOString();
        }
        result.monthly[doc.id] = data;
      });
    }

    console.log(
      `✅ Firestore: Loaded ${Object.keys(result.monthly).length} monthly totals for ${companyId}`,
    );
    return result;
  } catch (error) {
    console.error("❌ Firestore getAllAccountTotals error:", error);
    throw error;
  }
};

/**
 * Delete transactions by ID
 * @param {string} companyId
 * @param {Array<string>} transactionIds
 * @returns {Promise<{success: boolean, deleted: number}>}
 */
export const deleteTransactions = async (companyId, transactionIds) => {
  if (
    !companyId ||
    !Array.isArray(transactionIds) ||
    transactionIds.length === 0
  ) {
    throw new Error("Invalid companyId or transactionIds");
  }

  const batch = writeBatch(db);
  let deleted = 0;

  try {
    for (const txId of transactionIds) {
      const txRef = doc(db, `companies/${companyId}/transactions`, txId);
      batch.delete(txRef);
      deleted++;
    }

    await batch.commit();

    console.log(`✅ Firestore: Deleted ${deleted} transactions`);

    // Note: Totals are NOT automatically decremented here.
    // Consider triggering a recalculation or handling deletes with Cloud Functions.

    return { success: true, deleted };
  } catch (error) {
    console.error("❌ Firestore deleteTransactions error:", error);
    throw error;
  }
};

/**
 * Recalculate totals from scratch (useful after bulk deletes or corrections)
 * @param {string} companyId
 * @returns {Promise<{success: boolean, monthlyPeriods: number, periods: Array}>}
 */
export const recalculateTotals = async (companyId) => {
  if (!companyId) {
    throw new Error("companyId is required");
  }

  try {
    console.log(`🔄 Recalculating monthly totals for ${companyId}...`);

    // Fetch all transactions
    const txCollection = collection(db, `companies/${companyId}/transactions`);
    const snapshot = await getDocs(txCollection);

    if (snapshot.empty) {
      console.log("⚠️ No transactions found for recalculation");
      return { success: true, message: "No transactions to recalculate" };
    }

    const totalsByMonth = {};

    snapshot.docs.forEach((doc) => {
      const tx = doc.data();

      // Use transaction date for monthly grouping
      const txDate = tx.date || tx.Date;
      if (!txDate) {
        console.warn("⚠️ Transaction missing date field:", doc.id);
        return;
      }

      const month = txDate.slice(0, 7); // YYYY-MM format
      const accountGroup = tx.Account_group || "No Account";
      const accountName =
        tx.Account || tx.account || tx.accountNames || accountGroup;
      const amount = Number(tx.Opening_balance) || 0;
      const amountVatExclusive =
        Number(tx.Opening_balance_Vat_Exclusive) || amount;
      const vatAmount = Number(tx.vatAmount) || 0;

      // ========== MONTHLY TOTALS ==========
      if (!totalsByMonth[month]) {
        totalsByMonth[month] = {
          period: month,
          periodType: "monthly",
          accounts: {},
          accountGroups: {},
        };
      }

      // Store per-account balance for the month
      if (!totalsByMonth[month].accounts[accountName]) {
        totalsByMonth[month].accounts[accountName] = {
          category: accountGroup,
          balance: 0,
          balanceVatExclusive: 0,
          vatAmount: 0,
          count: 0,
        };
      }
      totalsByMonth[month].accounts[accountName].balance += amount;
      totalsByMonth[month].accounts[accountName].balanceVatExclusive +=
        amountVatExclusive;
      totalsByMonth[month].accounts[accountName].vatAmount += vatAmount;
      totalsByMonth[month].accounts[accountName].count += 1;

      // Aggregate by account group for the month
      if (!totalsByMonth[month].accountGroups[accountGroup]) {
        totalsByMonth[month].accountGroups[accountGroup] = {
          balance: 0,
          count: 0,
          accounts: [],
        };
      }
      totalsByMonth[month].accountGroups[accountGroup].balance += amount;
      totalsByMonth[month].accountGroups[accountGroup].count += 1;

      if (
        !totalsByMonth[month].accountGroups[accountGroup].accounts.includes(
          accountName,
        )
      ) {
        totalsByMonth[month].accountGroups[accountGroup].accounts.push(
          accountName,
        );
      }
    });

    // Write monthly totals to Firestore in batches (Firestore limit: 500 operations per batch)
    const batchSize = 500;
    const monthPeriods = Object.entries(totalsByMonth);

    for (let i = 0; i < monthPeriods.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = monthPeriods.slice(i, i + batchSize);

      chunk.forEach(([month, totals]) => {
        const totalsRef = doc(
          db,
          `companies/${companyId}/accountTotals_monthly`,
          month,
        );
        batch.set(totalsRef, {
          ...totals,
          lastUpdated: Timestamp.now(),
          lastRecalculated: Timestamp.now(),
        });
      });

      await batch.commit();
    }

    console.log(
      `✅ Firestore: Recalculated ${Object.keys(totalsByMonth).length} monthly periods`,
    );

    return {
      success: true,
      monthlyPeriods: Object.keys(totalsByMonth).length,
      periods: Object.keys(totalsByMonth),
    };
  } catch (error) {
    console.error("❌ Firestore recalculateTotals error:", error);
    throw error;
  }
};

/**
 * Real-time listener for transactions (optional for live updates)
 * @param {string} companyId
 * @param {Function} callback - Called with array of transactions on updates
 * @param {Object} filters - Same as getTransactions filters
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToTransactions = (companyId, callback, filters = {}) => {
  if (!companyId || typeof callback !== "function") {
    throw new Error("companyId and callback are required");
  }

  const {
    startDate,
    endDate,
    accountGroup,
    orderByField = "date",
    orderDirection = "asc",
  } = filters;

  try {
    let q = collection(db, `companies/${companyId}/transactions`);
    const constraints = [];

    if (startDate) constraints.push(where("date", ">=", startDate));
    if (endDate) constraints.push(where("date", "<=", endDate));
    if (accountGroup)
      constraints.push(where("Account_group", "==", accountGroup));
    constraints.push(orderBy(orderByField, orderDirection));

    q = query(q, ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const transactions = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString?.() || null,
        }));
        callback(transactions);
      },
      (error) => {
        console.error("❌ Firestore subscription error:", error);
      },
    );

    console.log(`✅ Firestore: Subscribed to transactions for ${companyId}`);
    return unsubscribe;
  } catch (error) {
    console.error("❌ Firestore subscribeToTransactions error:", error);
    throw error;
  }
};

/**
 * Update a single transaction
 * @param {string} companyId
 * @param {string} transactionId
 * @param {Object} updates - Fields to update
 * @param {boolean} updateTotals - Whether to recalculate totals (default: false, use for critical updates only)
 * @returns {Promise<{success: boolean}>}
 */
export const updateTransaction = async (
  companyId,
  transactionId,
  updates,
  updateTotals = false,
) => {
  if (!companyId || !transactionId) {
    throw new Error("companyId and transactionId are required");
  }

  try {
    const txRef = doc(db, `companies/${companyId}/transactions`, transactionId);

    await setDoc(
      txRef,
      {
        ...updates,
        updatedAt: Timestamp.now(),
      },
      { merge: true },
    );

    console.log(`✅ Firestore: Updated transaction ${transactionId}`);

    // Optionally recalculate totals (expensive, use sparingly)
    if (updateTotals) {
      await recalculateTotals(companyId);
    }

    return { success: true };
  } catch (error) {
    console.error("❌ Firestore updateTransaction error:", error);
    throw error;
  }
};

/**
 * Batch update multiple transactions (for efficient bulk edits)
 * @param {string} companyId
 * @param {Array} operations - Array of { id, update } objects
 * @param {Object} options - { updateTotals: false, chunkSize: 500 }
 * @returns {Promise<{success: boolean, updated: number}>}
 */
export const bulkUpdateTransactions = async (
  companyId,
  operations,
  options = {},
) => {
  const { updateTotals = false, chunkSize = 500 } = options;

  if (!companyId || !Array.isArray(operations) || operations.length === 0) {
    throw new Error("Invalid companyId or operations array");
  }

  try {
    let totalUpdated = 0;
    const chunks = [];

    // Split operations into chunks (Firestore batch limit is 500)
    // Each allocation can write a transaction, a history event and a rule.
    const hasAllocationSidecars = operations.some(
      (op) => op.allocationEvent || op.allocationRule,
    );
    const safeChunkSize = Math.max(
      1,
      Math.min(chunkSize, hasAllocationSidecars ? 150 : 500),
    );
    for (let i = 0; i < operations.length; i += safeChunkSize) {
      chunks.push(operations.slice(i, i + safeChunkSize));
    }

    console.log(
      `🔄 Firestore: Batch updating ${operations.length} transactions in ${chunks.length} chunks`,
    );

    // Process each chunk
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      let chunkUpdated = 0;

      chunk.forEach(({ id, update, allocationEvent, allocationRule }) => {
        if (!id || !update) {
          console.warn("⚠️ Skipping invalid operation:", { id, update });
          return;
        }

        const txRef = doc(db, `companies/${companyId}/transactions`, id);
        batch.update(txRef, {
          ...update,
          updatedAt: Timestamp.now(),
        });
        if (allocationEvent) {
          batch.set(
            doc(
              db,
              "companies",
              companyId,
              "allocationHistory",
              allocationEvent.id,
            ),
            allocationEvent,
          );
        }
        if (allocationRule) {
          batch.set(
            doc(
              db,
              "companies",
              companyId,
              "allocationRules",
              allocationRule.id,
            ),
            allocationRule,
          );
        }
        chunkUpdated++;
      });

      await batch.commit();
      totalUpdated += chunkUpdated;
      console.log(
        `✅ Firestore: Chunk ${chunkIndex + 1}/${
          chunks.length
        } completed (${chunkUpdated} updates)`,
      );
    }

    console.log(
      `✅ Firestore: Batch update completed - ${totalUpdated} transactions updated`,
    );

    // Optionally recalculate totals after bulk updates
    if (updateTotals) {
      console.log("🔄 Recalculating totals after batch update...");
      await recalculateTotals(companyId);
    }

    return { success: true, updated: totalUpdated };
  } catch (error) {
    console.error("❌ Firestore bulkUpdateTransactions error:", error);
    throw error;
  }
};

/**
 * Get account-level detail for a specific category
 * @param {string} companyId
 * @param {string} period - e.g., "2024-03" or "2024"
 * @param {string} category - e.g., "Income", "Expenses", "Current Assets"
 * @returns {Promise<Array>} - Array of accounts with balances: [{ accountName, balance, count }, ...]
 */
export const getAccountsByCategory = async (companyId, period, category) => {
  if (!companyId || !period || !category) {
    throw new Error("companyId, period, and category are required");
  }

  try {
    const totals = await getAccountTotals(companyId, period);
    if (!totals || !totals.accounts) {
      return [];
    }

    // Filter accounts by category and return sorted by balance (descending)
    const accountsInCategory = Object.entries(totals.accounts)
      .filter(([accountName, data]) => data.category === category)
      .map(([accountName, data]) => ({
        accountName,
        balance: data.balance,
        count: data.count,
        category: data.category,
      }))
      .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance)); // Sort by absolute balance descending

    console.log(
      `✅ Firestore: Retrieved ${accountsInCategory.length} accounts for ${category} in ${period}`,
    );
    return accountsInCategory;
  } catch (error) {
    console.error("❌ Firestore getAccountsByCategory error:", error);
    throw error;
  }
};

/**
 * Get accounts with non-zero balances (for financial statement display)
 * @param {string} companyId
 * @param {string} period - e.g., "2024-03" or "2024"
 * @param {string} category - Optional: filter by category
 * @returns {Promise<Array>} - Array of accounts with non-zero balances
 */
export const getAccountsWithBalances = async (
  companyId,
  period,
  category = null,
) => {
  if (!companyId || !period) {
    throw new Error("companyId and period are required");
  }

  try {
    const totals = await getAccountTotals(companyId, period);
    if (!totals || !totals.accounts) {
      return [];
    }

    // Get all accounts with non-zero balances
    const accountsWithBalances = Object.entries(totals.accounts)
      .filter(([accountName, data]) => {
        const matchesCategory = category ? data.category === category : true;
        return data.balance !== 0 && matchesCategory;
      })
      .map(([accountName, data]) => ({
        accountName,
        balance: data.balance,
        count: data.count,
        category: data.category,
      }))
      .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));

    console.log(
      `✅ Firestore: Retrieved ${accountsWithBalances.length} accounts with non-zero balances`,
    );
    return accountsWithBalances;
  } catch (error) {
    console.error("❌ Firestore getAccountsWithBalances error:", error);
    throw error;
  }
};

/**
 * Convert account totals to transaction-like objects for backward compatibility
 * Enables components to continue using .filter(tx => tx.Account_group === "Sales").reduce(...)
 *
 * @param {string} companyId - Company ID
 * @param {string} period - Period identifier (YYYY-MM or YYYY)
 * @returns {Promise<Array>} Array of mock transaction objects with Account_group and Opening_balance
 *
 * @example
 * // Use in components instead of loading all transactions:
 * const AllYearsBalanceSOFP = await firestoreAdapter.getTransactionsFromTotals(companyId, "2024-03");
 *
 * // Then use existing filter/reduce patterns:
 * const salesTotal = AllYearsBalanceSOFP
 *   .filter(tx => tx.Account_group === "Sales")
 *   .reduce((sum, tx) => sum + tx.Opening_balance, 0);
 */
export const getTransactionsFromTotals = async (companyId, period) => {
  if (!companyId || !period) {
    throw new Error("companyId and period are required");
  }

  try {
    const totals = await getAccountTotals(companyId, period);
    if (!totals || !totals.accounts) {
      return [];
    }

    // Helper to get last day of period for date filtering compatibility
    const getPeriodEndDate = (p) => {
      if (!p) return new Date().toISOString().split("T")[0];
      // If yearly period (e.g. "2024"), we should ideally not use it for company years
      // but if we must, return end of that year.
      if (p.length === 4) return `${p}-12-31`;

      // For monthly period (e.g. "2024-03"), return last day of that month
      const [y, m] = p.split("-");
      // new Date(year, monthIndex, 0) returns the last day of the previous month
      // monthIndex is 1-based from split, so passing it directly gives last day of that month
      // e.g. new Date(2024, 3, 0) -> March 31st (since 3 is April in 0-indexed months)
      const lastDay = new Date(y, m, 0).getDate();
      return `${p}-${lastDay}`;
    };

    const periodDate = getPeriodEndDate(period);

    // Convert accounts object to array of mock transaction objects
    const mockTransactions = Object.entries(totals.accounts).map(
      ([accountName, data]) => ({
        Account_group: accountName, // Account name (e.g., "Sales", "Bank")
        Opening_balance: data.balance, // Account balance (gross, including VAT)
        Opening_balance_Vat_Exclusive: data.balanceVatExclusive, // VAT-exclusive balance
        vatAmount: data.vatAmount || 0, // VAT amount for Trial Balance and SOFP
        count: data.count, // Number of transactions
        category: data.category, // Category for reference (e.g., "Income")
        date: periodDate, // Add date for filtering compatibility
        // Mock transaction metadata
        _isMockTransaction: true,
        _period: period,
        _source: "firestoreTotals",
      }),
    );

    console.log(
      `✅ Firestore: Converted ${mockTransactions.length} accounts to mock transactions`,
    );
    return mockTransactions;
  } catch (error) {
    console.error("❌ Firestore getTransactionsFromTotals error:", error);
    throw error;
  }
};

/**
 * Get transaction count (for pagination/stats)
 * @param {string} companyId
 * @param {Object} filters
 * @returns {Promise<number>}
 */
export const getTransactionCount = async (companyId, filters = {}) => {
  if (!companyId) {
    throw new Error("companyId is required");
  }

  try {
    const transactions = await getTransactions(companyId, filters);
    return transactions.length;
  } catch (error) {
    console.error("❌ Firestore getTransactionCount error:", error);
    throw error;
  }
};

const firestoreAdapter = {
  bulkWriteTransactions,
  getTransactions,
  getAccountTotals,
  getMonthlyTotals,
  getYearlyTotals,
  getAllAccountTotals,
  getAccountsByCategory, // NEW: Get accounts within a category
  getAccountsWithBalances, // NEW: Get accounts with non-zero balances
  getTransactionsFromTotals, // NEW: Convert totals to mock transactions for compatibility
  deleteTransactions,
  recalculateTotals,
  subscribeToTransactions,
  updateTransaction,
  getTransactionCount,
};

export default firestoreAdapter;
